import React from "react";
import { primaryColor } from "@/lib/theme";

/**
 * The four text devices of the encomendas flow.
 *
 * These replace a single treatment — `text-[11px] font-bold uppercase
 * tracking-[0.1em] text-[#6C7C93]` — that was used ~113 times to do four
 * unrelated jobs at once. A unit suffix ("kg") was rendered identically to a
 * page section ("Resumo"), so the page had no hierarchy to read.
 *
 * Hierarchy is carried by size, weight and color. Never by capitals or
 * letter-spacing: Portuguese labels are long ("Informações adicionais"),
 * and tracked-out caps make them longer and slower to scan.
 */

/**
 * Top of a card. The icon tile is the section marker and carries meaning on
 * its own (a pin for the route, a box for the parcel), so there is no label
 * above the title — that was a second device repeating the first.
 */
export const SectionHeader = ({
  title,
  description,
  icon,
  action,
  className = "",
}: {
  title: string;
  /**
   * Linha de apoio sob o título, para o que o título sozinho não diz (quantos
   * pacotes a cotação cobre, por exemplo). Sem ela o cabeçalho fica idêntico
   * ao de antes — é só o título.
   */
  description?: string;
  icon: React.ReactNode;
  /** Optional trailing control, right-aligned on the title row. */
  action?: React.ReactNode;
  className?: string;
}) => (
  <div className={`px-6 pt-5 ${className}`}>
    <div className="flex items-center gap-3">
      <div
        className="flex size-10 shrink-0 items-center justify-center rounded-control"
        style={{ backgroundColor: `${primaryColor}1A` }}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-lede font-bold text-ink">{title}</h2>
        {description && (
          <p className="mt-0.5 text-fine text-slate">{description}</p>
        )}
      </div>
      {action}
    </div>
    <div className="mt-4 h-px bg-line" />
  </div>
);

/**
 * A block inside a card ("Itens no pacote", "Participantes"). Separated from
 * a field label by weight and color alone, which is enough at the same size.
 */
export const SubHeader = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <h3 className={`text-fine font-semibold text-ink ${className}`}>
    {children}
  </h3>
);

/** Names an input or a value. Sentence case, quiet, never competing. */
export const FieldLabel = ({
  children,
  htmlFor,
  className = "",
}: {
  children: React.ReactNode;
  htmlFor?: string;
  className?: string;
}) => (
  <label
    htmlFor={htmlFor}
    className={`block text-fine text-slate ${className}`}
  >
    {children}
  </label>
);

/**
 * A unit or currency marker attached to a value (kg, uni, R$). Sits at the
 * trailing edge of its field so it reads as part of the number, not as a
 * heading standing over it.
 */
export const Unit = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => <span className={`text-micro text-mist ${className}`}>{children}</span>;
